import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from './LanguageContext';

const APYGroupLayout = ({ children }) => {
  const location = useLocation();
  const { language, changeLanguage, t } = useLanguage(); // Usamos el contexto de idioma
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Actualizamos el estado cuando cambia la ubicación
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Languages available
  const languages = ['ES', 'EN', 'JPN'];

  // Navigation items usando traducciones
  const navItems = [
    { path: '/', label: t('nav.home') },
    { path: '/compania', label: t('nav.company') },
    { path: '/atletas', label: t('nav.athletes') },
    { path: '/servicio', label: t('nav.service') },
    { path: '/contacto', label: t('nav.contact') },
  ];

  // Verificar si estamos en la ruta actual
  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="flex flex-col min-h-screen bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black shadow-md">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="https://pub-8b7376b23b5a4b558c0089505b9b4b8e.r2.dev/logo.png" 
                alt="AFY GROUP" 
                className="h-10 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`font-medium text-sm uppercase tracking-wider transition-colors duration-300 ${
                    isActive(item.path)
                      ? 'text-red-600 font-bold'
                      : 'text-white hover:text-red-600'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Language Selector */}
            <div className="hidden md:flex items-center space-x-2">
              {languages.map((lang) => (
                <button
                  key={lang}
                  onClick={() => changeLanguage(lang)}
                  className={`px-2 py-1 text-xs font-medium rounded transition-colors duration-300 ${
                    language === lang
                      ? 'bg-red-600 text-white'
                      : 'text-white hover:text-red-600'
                  }`}
                >
                  {lang}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white focus:outline-none"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                {mobileMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black border-t border-gray-800">
            <div className="container mx-auto px-4 py-2">
              <nav className="flex flex-col space-y-4 py-4">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`font-medium text-sm uppercase tracking-wider transition-colors duration-300 ${
                      isActive(item.path)
                        ? 'text-red-600 font-bold'
                        : 'text-white hover:text-red-600'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>

              <div className="flex items-center space-x-2 py-4 border-t border-gray-800">
                {languages.map((lang) => (
                  <button
                    key={lang}
                    onClick={() => {
                      changeLanguage(lang);
                      setMobileMenuOpen(false);
                    }}
                    className={`px-2 py-1 text-xs font-medium rounded transition-colors duration-300 ${
                      language === lang
                        ? 'bg-red-600 text-white'
                        : 'text-white hover:text-red-600'
                    }`}
                  >
                    {lang}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer - Mejorado con Monte Fuji */}
      <footer className="relative text-white overflow-hidden">
        {/* Fondo con Monte Fuji */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/api/placeholder/1920/600" 
            alt="Monte Fuji" 
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-red-600 via-red-700 to-red-900 opacity-80"></div>
          <div className="absolute inset-0 bg-black opacity-40"></div>
        </div>
        
        {/* Contenido del footer */}
        <div className="container mx-auto px-4 py-16 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Columna 1: Logo e información */}
            <div className="transform hover:scale-105 transition-transform duration-300">
              <div className="bg-black/40 backdrop-blur-sm p-6 rounded-xl border border-white/10 shadow-xl h-full">
                <div className="flex items-center mb-6">
                  <img 
                    src="https://pub-8b7376b23b5a4b558c0089505b9b4b8e.r2.dev/logo2.png" 
                    alt="AFY GROUP" 
                    className="h-10 w-auto"
                  />
                </div>
                <p className="text-sm text-white/80 mb-6">
                  {t('footer.company.desc')}
                </p>
                <div className="flex space-x-4">
                  <a href="#" className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all duration-300">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z" />
                    </svg>
                  </a>
                  <a href="#" className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all duration-300">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                  </a>
                  <a href="#" className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all duration-300">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
            
            {/* Columna 2: Enlaces Rápidos */}
            <div className="transform hover:scale-105 transition-transform duration-300">
              <div className="bg-black/40 backdrop-blur-sm p-6 rounded-xl border border-white/10 shadow-xl h-full">
                <h3 className="text-xl font-bold mb-6 flex items-center">
                  <span className="bg-red-500 rounded-md w-2 h-8 mr-3"></span>
                  {t('footer.quickLinks')}
                </h3>
                <ul className="space-y-4">
                  {navItems.map((item) => (
                    <li key={item.path} className="transform transition-transform hover:translate-x-2 duration-300">
                      <a 
                        href={item.path}
                        className="flex items-center group"
                      >
                        <span className="w-2 h-2 bg-red-500 rounded-full mr-3 group-hover:bg-white transition-colors"></span>
                        <span className="text-white group-hover:text-yellow-200 transition-colors">{item.label}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            {/* Columna 3: Contacto */}
            <div className="transform hover:scale-105 transition-transform duration-300">
              <div className="bg-black/40 backdrop-blur-sm p-6 rounded-xl border border-white/10 shadow-xl h-full">
                <h3 className="text-xl font-bold mb-6 flex items-center">
                  <span className="bg-red-500 rounded-md w-2 h-8 mr-3"></span>
                  {t('footer.contact')}
                </h3>
                <ul className="space-y-5">
                  <li className="flex items-start group">
                    <div className="mr-3 mt-1 bg-red-500/30 p-1 rounded-lg group-hover:bg-red-500/60 transition-all">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      </svg>
                    </div>
                    <span className="text-white group-hover:text-yellow-200 transition-colors">San Isidro, Lima - Perú</span>
                  </li>
                  <li className="flex items-start group">
                    <div className="mr-3 mt-1 bg-red-500/30 p-1 rounded-lg group-hover:bg-red-500/60 transition-all">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                      </svg>
                    </div>
                    <a href="mailto:CONTACTO@AFYGROUP.PE" className="text-white group-hover:text-yellow-200 transition-colors">
                      CONTACTO@AFYGROUP.PE
                    </a>
                  </li>
                  <li className="group">
                    <div className="flex items-start mb-2">
                      <div className="mr-3 mt-1 bg-red-500/30 p-1 rounded-lg group-hover:bg-red-500/60 transition-all">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                        </svg>
                      </div>
                      <div className="flex flex-col">
                        <a href="tel:+51952441709" className="flex items-center space-x-2 text-white group-hover:text-yellow-200 transition-colors">
                          <span className="font-semibold px-2 py-1 bg-red-800/50 rounded text-xs">PER</span>
                          <span>+51 952 441 709</span>
                        </a>
                        <a href="tel:+817055959410" className="flex items-center space-x-2 text-white group-hover:text-yellow-200 transition-colors mt-3">
                          <span className="font-semibold px-2 py-1 bg-red-800/50 rounded text-xs">JPN</span>
                          <span>+81 70-5595-9410</span>
                        </a>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="mt-16 pt-6 text-center text-sm border-t border-white/20">
            <p className="text-white/80">&copy; {new Date().getFullYear()} AFY GROUP. {t('footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default APYGroupLayout;